import apiClient from './client';
import {ICleanUp, ISpot} from '../models/cleanUp';

// ─── Existing read/approve endpoints ────────────────────────────────────────

export const fetchCleanUById = async (id: string): Promise<ICleanUp> => {
    try {
        const response = await apiClient.get<ICleanUp>(`/clean-up/${id}`);
        return response.data;
    } catch (e) {
        throw e;
    }
};

export const fetchCleanUByDate = async (date: string): Promise<ICleanUp[]> => {
    try {
        const response = await apiClient.get<ICleanUp[]>(`/clean-up?date=${date}`);
        return response.data;
    } catch (e) {
        throw e;
    }
};

export const approveCleanUpSpot = async (cleanUpId: string, spotId: string): Promise<ICleanUp> => {
    try {
        const response = await apiClient.post<ICleanUp>(
            `/clean-up/${cleanUpId}/image-spots/${spotId}/approve`
        );
        return response.data;
    } catch (e) {
        throw e;
    }
};

export const unApproveCleanUpSpot = async (
    cleanUpId: string,
    spotId: string
): Promise<ICleanUp> => {
    try {
        const response = await apiClient.post<ICleanUp>(
            `/clean-up/${cleanUpId}/image-spots/${spotId}/unapprove`
        );
        return response.data;
    } catch (e) {
        throw e;
    }
};

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface ICreateCleanUpDto {
    programId: string;
    date: string;
    activityId?: string;
    /** Admin/Director only: assign a program Collector as created_by */
    collectorId?: string;
    /** Provide either locationId (existing) or location (new coordinates) */
    locationId?: string;
    location?: {latitude: number; longitude: number};
    area?: number;
    length?: number;
}

export interface IUpdateCleanUpDto {
    num_of_participant?: number;
    location_name?: string;
    notes?: string | null;
    district?: string | null;
    municipality?: string | null;
    city_island?: string | null;
    duration?: {
        from: {hour: number; minute: number};
        to: {hour: number; minute: number};
    };
    substrate_id?: string;
    team_id?: string | null;
    activityId?: string | null;
    area?: number | null;
    length?: number | null;
}

export interface IUpdateLocationDto {
    location?: {latitude: number; longitude: number};
}

export interface ICollectedWeightItem {
    waste_type: string;
    dry?: number;
    wet?: number;
}

export interface ICollectedWeightDto {
    no_litter_found: boolean;
    collected_weight?: ICollectedWeightItem[];
}

export interface ICollectedItemRecord {
    waste_category_item: string;
    amount: number;
}

export interface ICollectedItemsDto {
    waste_category: string;
    items: ICollectedItemRecord[];
}

export interface ILitterBagRecord {
    resource_id: string;
    full?: number;
    half?: number;
}

export interface ILitterBagsDto {
    resources: ILitterBagRecord[];
}

export interface IUsedResourceRecord {
    resource_id: string;
    amount: number;
}

export interface IUsedResourcesDto {
    resources: IUsedResourceRecord[];
}

export interface ISiteTypesDto {
    site_types: string[];
}

export interface ILocationInfoDto {
    location_infos: string[];
}

export interface IWeatherConditionRecord {
    condition_id: string;
    option_id?: string | null;
    value?: number;
}

export interface IWeatherConditionsDto {
    conditions: IWeatherConditionRecord[];
}

// ─── CRUD + sub-resource endpoints ──────────────────────────────────────────
// NOTE: These require Role.Admin / Role.Director on the backend.
// Backend role expansion (Phase 1) must be deployed before these calls succeed.

export const createCleanUp = async (dto: ICreateCleanUpDto): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>('/clean-up', dto);
    return response.data;
};

export const updateCleanUp = async (id: string, dto: IUpdateCleanUpDto): Promise<ICleanUp> => {
    const response = await apiClient.patch<ICleanUp>(`/clean-up/${id}`, dto);
    return response.data;
};

export const updateLocation = async (
    id: string,
    dto: IUpdateLocationDto
): Promise<ICleanUp> => {
    const response = await apiClient.patch<ICleanUp>(`/clean-up/${id}/location`, dto);
    return response.data;
};

export const deleteCleanUp = async (id: string): Promise<void> => {
    await apiClient.delete(`/clean-up/${id}`);
};

export const updateCollectedWeight = async (
    id: string,
    dto: ICollectedWeightDto
): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/collected-weight`, dto);
    return response.data;
};

export const updateCollectedItems = async (
    id: string,
    dto: ICollectedItemsDto
): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/collected-items`, dto);
    return response.data;
};

export const updateLitterBags = async (id: string, dto: ILitterBagsDto): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/litter-bags`, dto);
    return response.data;
};

export const updateResources = async (id: string, dto: IUsedResourcesDto): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/resources`, dto);
    return response.data;
};

export const updateSiteTypes = async (id: string, dto: ISiteTypesDto): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/site-types`, dto);
    return response.data;
};

export const updateAdditionalInfo = async (
    id: string,
    dto: ILocationInfoDto
): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(
        `/clean-up/${id}/additional-location-info`,
        dto
    );
    return response.data;
};

export const updateWeatherConditions = async (
    id: string,
    dto: IWeatherConditionsDto
): Promise<ICleanUp> => {
    const response = await apiClient.post<ICleanUp>(`/clean-up/${id}/weather-conditions`, dto);
    return response.data;
};

// ─── Image spots ─────────────────────────────────────────────────────────────

export const addImageSpot = async (cleanUpId: string): Promise<ISpot[]> => {
    const response = await apiClient.post<ISpot[]>(`/clean-up/${cleanUpId}/image-spots`);
    return response.data;
};

export const deleteImageSpot = async (cleanUpId: string, spotId: string): Promise<ISpot[]> => {
    const response = await apiClient.delete<ISpot[]>(
        `/clean-up/${cleanUpId}/image-spots/${spotId}`
    );
    return response.data;
};

export const uploadSpotImage = async (
    cleanUpId: string,
    spotId: string,
    type: 'before' | 'after',
    file: File
): Promise<ISpot[]> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await apiClient.post<ISpot[]>(
        `/clean-up/${cleanUpId}/image-spots/${spotId}/upload`,
        formData
    );
    return response.data;
};

export const replaceSpotImage = async (
    cleanUpId: string,
    spotId: string,
    type: 'before' | 'after',
    file: File
): Promise<ISpot[]> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await apiClient.patch<ISpot[]>(
        `/clean-up/${cleanUpId}/image-spots/${spotId}/upload`,
        formData
    );
    return response.data;
};

export const deleteSpotImage = async (
    cleanUpId: string,
    spotId: string,
    imageId: string
): Promise<ISpot[]> => {
    const response = await apiClient.delete<ISpot[]>(
        `/clean-up/${cleanUpId}/image-spots/${spotId}/images/${imageId}`
    );
    return response.data;
};
